import React, { useState } from 'react';
import { Typography, Card, Form, Input, Button, message } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { AppDispatch } from '../store';
import { updateUser } from '../store/slices/authSlice';
import { changePassword } from '../services/authService';

const { Title, Text } = Typography;

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F9FAFB;
  padding: 24px;
`;

const StyledCard = styled(Card)`
  width: 100%;
  max-width: 520px;

  .ant-card-body {
    padding: 40px;

    @media (max-width: 768px) {
      padding: 24px;
    }
  }
`;

const FormSection = styled.div`
  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: #000000;
    margin-bottom: 8px;
  }

  .section-description {
    font-size: 14px;
    color: #6B7280;
    margin-bottom: 24px;
  }
`;

const PasswordRequirementItem = styled.div<{ $passed: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$passed ? '#0C827A' : '#848787'};
  margin-bottom: 6px;

  .anticon {
    font-size: 14px;
    color: ${props => props.$passed ? '#0C827A' : '#848787'};
  }
`;

const ChangePasswordPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [passwordForm] = Form.useForm();
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [isPasswordFormValid, setIsPasswordFormValid] = useState(false);

  const pwHasMinLength = newPassword.length >= 16;
  const pwHasNumber = /\d/.test(newPassword);
  const pwHasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword);
  const pwHasUpperAndLower = /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword);

  const handlePasswordFormChange = (_changedValues: any, allValues: any) => {
    const current = allValues.currentPassword?.trim() || '';
    const pwd = allValues.newPassword?.trim() || '';
    const confirm = allValues.confirmPassword?.trim() || '';
    setNewPassword(allValues.newPassword || '');
    const meetsRequirements =
      pwd.length >= 16 &&
      /\d/.test(pwd) &&
      /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pwd) &&
      /[A-Z]/.test(pwd) && /[a-z]/.test(pwd);
    setIsPasswordFormValid(current.length > 0 && meetsRequirements && pwd === confirm);
  };

  const handlePasswordChange = async () => {
    try {
      const values = await passwordForm.validateFields();
      setIsSavingPassword(true);

      await changePassword({
        currentPassword: values.currentPassword,
        password: values.newPassword,
        passwordConfirmation: values.confirmPassword,
      });

      message.success('Password changed successfully');
      dispatch(updateUser({ passwordChangeRequired: false }));
    } catch (error: any) {
      if (error?.code === 'VALIDATION_ERROR' && error?.data) {
        const fieldErrors: any[] = [];
        const toastMessages: string[] = [];
        const fieldMap: Record<string, string> = {
          currentPassword: 'currentPassword',
          password: 'newPassword',
          passwordConfirmation: 'confirmPassword',
        };
        for (const [apiField, formField] of Object.entries(fieldMap)) {
          if (error.data[apiField] && Array.isArray(error.data[apiField])) {
            fieldErrors.push({ name: formField, errors: error.data[apiField] });
            toastMessages.push(...error.data[apiField]);
          }
        }
        if (fieldErrors.length > 0) {
          passwordForm.setFields(fieldErrors);
          message.error(toastMessages.join('. '));
          return;
        }
      }
      message.error(error?.message || 'Failed to change password');
    } finally {
      setIsSavingPassword(false);
    }
  };

  return (
    <PageWrapper>
      <StyledCard>
        <FormSection>
          <Title level={3} style={{ marginBottom: 8 }}>Change Password</Title>
          <div className="section-description">
            You are required to change your password before continuing. Please enter your current password and choose a new one.
          </div>

          <Form
            form={passwordForm}
            layout="vertical"
            onValuesChange={handlePasswordFormChange}
            autoComplete="off"
            requiredMark={false}
          >
            <Form.Item
              label="Current Password"
              name="currentPassword"
              rules={[{ required: true, message: 'Current password is required' }]}
            >
              <Input.Password placeholder="Enter current password" autoComplete="current-password" />
            </Form.Item>

            <Form.Item
              label="New Password"
              name="newPassword"
              rules={[{ required: true, message: 'New password is required' }]}
            >
              <Input.Password placeholder="Enter new password" autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              label="Confirm New Password"
              name="confirmPassword"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Please confirm your new password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Passwords do not match'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm new password" autoComplete="new-password" />
            </Form.Item>

            <div style={{ marginBottom: 16 }}>
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 500, display: 'block', marginBottom: 8 }}>
                Password must contain the following:
              </Text>
              <PasswordRequirementItem $passed={pwHasMinLength}>
                <CheckCircleFilled />
                <span>At least 16 characters</span>
              </PasswordRequirementItem>
              <PasswordRequirementItem $passed={pwHasNumber}>
                <CheckCircleFilled />
                <span>At least one number</span>
              </PasswordRequirementItem>
              <PasswordRequirementItem $passed={pwHasSpecialChar}>
                <CheckCircleFilled />
                <span>At least one special character</span>
              </PasswordRequirementItem>
              <PasswordRequirementItem $passed={pwHasUpperAndLower}>
                <CheckCircleFilled />
                <span>At least one uppercase and lowercase character</span>
              </PasswordRequirementItem>
            </div>

            <Button
              type="primary"
              onClick={handlePasswordChange}
              loading={isSavingPassword}
              disabled={!isPasswordFormValid}
              block
            >
              Change Password
            </Button>
          </Form>
        </FormSection>
      </StyledCard>
    </PageWrapper>
  );
};

export default ChangePasswordPage;
